/**
 * What the focus views draw, separated from how the timer gets there.
 *
 * Every view in this folder takes a `FocusViewModel` (or part of one) rather
 * than the reducer state, so a story can put the card, the pill or the panel
 * into any state with plain args: no clock, no storage, no clicking through.
 * `toViewModel` is the one place reducer state becomes labels.
 */
import {
  elapsedMs,
  isBetweenRounds,
  remainingMs,
  roundInSet,
  type BreakKind,
  type FocusMode,
  type FocusPhase,
  type FocusState,
  type FocusView,
  type ParkingNote,
} from '@/lib/focus/focusMachine';
import { formatClock, toMinutes } from '@/lib/focus/format';

export interface FocusLessonOption {
  id: string;
  title: string;
}

export interface FinishedBlockView {
  focusMinutes: number;
  endedEarly: boolean;
  /** Minutes since a block that ran out unseen ended. 0 = it was open. */
  awayMinutes: number;
  intention?: string;
  recap?: string;
  recapDismissed?: boolean;
}

export interface FocusViewModel {
  phase: FocusPhase;
  mode: FocusMode;
  /** `18:42` left - or elapsed, for Flow focus. */
  timeLabel: string;
  /** 0–1 done, or `null` for Flow focus. */
  progress: number | null;
  round: { current: number; total: number } | null;
  intention?: string;
  lessonId?: string;
  breakKind: BreakKind | null;
  lastBlock: FinishedBlockView | null;
  parkingLot: ParkingNote[];
  /** Idle mid-session: a break ran out and the next round waits for Start. */
  between: boolean;
  /** How this session is seen, fixed at Start. */
  view: FocusView;
  seed: number;
  /** Where the sakura is: its bloom, and the petals of the last round. */
  sakura: SakuraProgress;
}

/**
 * The sakura's arc over a session: blossom fills in round by round until the
 * canopy is dense at the end of the second-last round; the last round is the
 * petals falling. A one-round session blooms for 75% of it, then falls.
 */
export interface SakuraProgress {
  /** 0-1: from the bare tree to the full canopy. */
  bloom: number;
  /** The last round is under way (or over): petals fall. */
  falling: boolean;
  /** 0-1 through the last round: how much of the fall has settled. */
  fall: number;
}

const clamp01 = (n: number) => Math.min(1, Math.max(0, n));

export function sakuraProgress(state: FocusState, now: number): SakuraProgress {
  const session = state.phase === 'idle' ? state.lastSession : null;
  const rounds = session?.rounds ?? state.rounds;
  const planned =
    state.phase === 'focus' || state.phase === 'paused'
      ? state.plannedMs
      : null;
  const current = planned ? clamp01(elapsedMs(state, now) / planned) : 0;
  const done = session ? session.blocks : state.completedRounds;
  if (state.phase === 'idle' && !session && !isBetweenRounds(state)) {
    return { bloom: 0, falling: false, fall: 0 };
  }
  if (rounds <= 1) {
    const c = session ? (done > 0 ? 1 : 0) : current;
    return {
      bloom: clamp01(c / 0.75),
      falling: c > 0.75,
      fall: clamp01((c - 0.75) / 0.25),
    };
  }
  const pos = done + current;
  return {
    bloom: clamp01(pos / (rounds - 1)),
    falling: pos > rounds - 1,
    fall: clamp01(pos - (rounds - 1)),
  };
}

export function toViewModel(state: FocusState, now: number): FocusViewModel {
  const left = remainingMs(state, now);
  const elapsed = elapsedMs(state, now);
  const progress =
    state.plannedMs === null || state.plannedMs === 0
      ? null
      : Math.min(1, elapsed / state.plannedMs);
  const block = state.lastBlock;
  return {
    phase: state.phase,
    mode: state.mode,
    timeLabel:
      state.phase === 'idle'
        ? formatClock(state.mode === 'flow' ? 0 : state.focusMinutes * 60_000)
        : formatClock(left ?? elapsed),
    progress: state.phase === 'idle' ? 0 : progress,
    round: roundInSet(state),
    intention: state.intention,
    lessonId: state.lessonId,
    breakKind: state.breakKind,
    lastBlock: block
      ? {
          focusMinutes: toMinutes(block.focusMs),
          endedEarly: block.endedEarly,
          awayMinutes: toMinutes(block.awayMs),
          intention: block.intention,
          recap: block.recap,
          recapDismissed: block.recapDismissed,
        }
      : null,
    parkingLot: state.parkingLot,
    between: isBetweenRounds(state),
    view: state.view,
    seed: state.seed,
    sakura: sakuraProgress(state, now),
  };
}

/** "25 minutes counts." for an early end, "Nice focus." otherwise. */
export function blockHeadline(block: FinishedBlockView): string {
  if (!block.endedEarly) return 'Nice focus.';
  const unit = block.focusMinutes === 1 ? 'minute' : 'minutes';
  return `${block.focusMinutes} ${unit} counts.`;
}

/** "25 min on Photosynthesis" / "25 minutes of focus". */
export function blockSummary(block: FinishedBlockView): string {
  return block.intention
    ? `${block.focusMinutes} min on ${block.intention}`
    : `${block.focusMinutes} minutes of focus`;
}

/** Blocks kept on this device because the server could not take them yet. */
export interface FocusSyncWaiting {
  pending: number;
  reason: 'offline' | 'error';
}

/** Today's line under the set-up. */
export interface FocusToday {
  blocks: number;
  minutes: number;
  waiting?: FocusSyncWaiting | null;
}

/** Only worth saying when something is actually waiting. */
export function syncWaiting(sync: {
  state: string;
  pending: number;
}): FocusSyncWaiting | null {
  if (sync.pending === 0) return null;
  if (sync.state === 'offline')
    return { pending: sync.pending, reason: 'offline' };
  if (sync.state === 'error') return { pending: sync.pending, reason: 'error' };
  return null;
}
